/**
 * BSC Costs — Reconciliation tab.
 *
 * Four invariants of the allocation model, each as a card that states its measured number,
 * not just a colour. A tick with no figure behind it is worthless: two of these invariants
 * are currently broken by a material amount and the point of the tab is to say by how much.
 */
import { CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import type { BscReconciliation } from '../../types/babBsc';
import { formatEur, formatEurExact } from '../../utils/babFormat';

interface Props {
    data: BscReconciliation;
}

function StatusCard({
    title,
    ok,
    headline,
    subline,
    children,
}: {
    title: string;
    ok: boolean;
    headline: string;
    subline?: string;
    children?: React.ReactNode;
}) {
    return (
        <div className={`bg-white rounded-lg shadow p-5 border-l-4 ${ok ? 'border-green-500' : 'border-amber-500'}`}>
            <div className="flex items-start gap-3">
                {ok
                    ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className={`mt-1 text-lg font-semibold ${ok ? 'text-gray-900' : 'text-amber-700'}`}>
                        {headline}
                    </p>
                    {subline && <p className="mt-0.5 text-xs text-gray-500">{subline}</p>}
                    {children && <div className="mt-3">{children}</div>}
                </div>
            </div>
        </div>
    );
}

export function BscReconciliationPanel({ data }: Props) {
    const { engineNetZero, poolCompleteness, frozenTieOut, ruleIntegrity } = data;
    const unruled = poolCompleteness.costCentres.filter(c => !c.hasActiveRule);
    const ruleIssues = ruleIntegrity.costCentres.filter(
        c => c.percentSumOff || c.notInBscMap || c.notInCostCentreMaster || c.missingTargetAccount
    );

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 1 — engine net zero -------------------------------------------------- */}
                <StatusCard
                    title="1. Allocation engine nets to zero"
                    ok={engineNetZero.ok}
                    headline={`${formatEurExact(engineNetZero.netEUR)} EUR net`}
                    subline={
                        `Storno and allocation cancel per period. Measured against stored `
                        + `DECIMAL(18,2) rows, so the bound is half a cent per row — not the `
                        + `engine's in-memory ${engineNetZero.engineToleranceEUR}.`
                    }
                >
                    {engineNetZero.lastRun && (
                        <p className="text-xs text-gray-500">
                            Last run {new Date(engineNetZero.lastRun.actionDate).toLocaleString('de-CH')}
                            {' by '}{engineNetZero.lastRun.actionBy}
                            {' — through '}{engineNetZero.lastRun.periodYear}-
                            {String(engineNetZero.lastRun.periodMonth).padStart(2, '0')}
                        </p>
                    )}
                </StatusCard>

                {/* 3 — live vs frozen --------------------------------------------------- */}
                <StatusCard
                    title="3. Live data ties to the published freeze"
                    ok={frozenTieOut.ok}
                    headline={
                        frozenTieOut.ok
                            ? `${frozenTieOut.cellCount} cells match`
                            : `${formatEur(frozenTieOut.diffEUR)} EUR apart`
                    }
                    subline={
                        frozenTieOut.ok
                            ? 'The published BAB reflects the current allocation.'
                            : `${frozenTieOut.breakCount} of ${frozenTieOut.cellCount} cells differ. `
                              + 'The freeze predates the last allocation run — the published BAB shows '
                              + 'a different allocation than the rules configured today.'
                    }
                />

                {/* 2 — pool completeness ------------------------------------------------ */}
                <StatusCard
                    title="2. Cost-centre pool is fully allocated"
                    ok={poolCompleteness.ok}
                    headline={`${formatEur(poolCompleteness.unruledRemainderEUR)} EUR not in the BAB`}
                    subline={
                        'Pool ' + formatEur(poolCompleteness.poolEUR) + ' EUR, allocated '
                        + formatEur(poolCompleteness.allocatedEUR) + ' EUR. A cost centre without an '
                        + 'active rule keeps its cost on the Personnel and Team-expense lines with no '
                        + 'business unit — and the report scope drops BU-less rows, so it lands neither '
                        + 'in Team cost nor in Business-Support.'
                    }
                />

                {/* 4 — rule integrity --------------------------------------------------- */}
                <StatusCard
                    title="4. Allocation rules are structurally sound"
                    ok={ruleIntegrity.ok}
                    headline={
                        ruleIntegrity.ok
                            ? `${ruleIntegrity.costCentreCount} cost centres clean`
                            : `${ruleIntegrity.issueCount} of ${ruleIntegrity.costCentreCount} with an issue`
                    }
                    subline="Percentages sum to 100, every rule names a target account, every allocating cost centre is mapped."
                />
            </div>

            {/* ---- stranded cost, the headline finding ------------------------------- */}
            {unruled.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Cost centres without an active rule — {formatEur(poolCompleteness.unruledRemainderEUR)} EUR
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            Year to date. Two different causes sit in this table, and only the second
                            is a gap. <span className="font-medium">Substituted or out by decision:</span>{' '}
                            dch-5040 carries the actual employer social contributions (AHV, UVG, KTG),
                            which the BAB deliberately replaces with the calculated 28.4 % / 20 %
                            surcharge — counting both would double-count. dch-5030 is non-operating.{' '}
                            <span className="font-medium">No substitute:</span> the dch-4xxx cost centres
                            carry IT outsourcing, salary and insurance incurred for a specific subsidiary;
                            they were allocated 100 % to it until 2025 and have no rule for 2026.
                        </p>
                        {poolCompleteness.strandedByLine.length > 0 && (
                            <p className="mt-2 text-xs text-gray-600">
                                <span className="font-medium">This is not BSC-line cost.</span> It sits on{' '}
                                {poolCompleteness.strandedByLine.map((l, i) => (
                                    <span key={l.lineCode}>
                                        {i > 0 && ', '}
                                        {l.lineCode} {l.lineName ?? ''} {formatEur(l.strandedEUR)} EUR
                                    </span>
                                ))}
                                . The allocation is what would have reclassified it into the
                                Business-Support block at the receiving business units.
                            </p>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium">Company</th>
                                    <th className="px-4 py-2 text-left font-medium">Cost centre</th>
                                    <th className="px-4 py-2 text-left font-medium">Name</th>
                                    <th className="px-4 py-2 text-right font-medium">Pool EUR</th>
                                    <th className="px-4 py-2 text-right font-medium">Allocated EUR</th>
                                    <th className="px-4 py-2 text-right font-medium">Not in BAB EUR</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {unruled.map(c => (
                                    <tr key={c.costCenterId} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-700">{c.companyCode}</td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-900">{c.costCenterId}</td>
                                        <td className="px-4 py-2 text-gray-700">
                                            {c.costCenterName ?? (
                                                <span className="text-amber-700">not in the cost-centre master</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{formatEur(c.poolEUR)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{formatEur(c.allocatedEUR)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-semibold text-amber-700">
                                            {formatEur(c.remainderEUR)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 font-semibold">
                                <tr>
                                    <td className="px-4 py-2 text-gray-900" colSpan={3}>Total</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                                        {formatEur(unruled.reduce((s, c) => s + c.poolEUR, 0))}
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                                        {formatEur(unruled.reduce((s, c) => s + c.allocatedEUR, 0))}
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums text-amber-700">
                                        {formatEur(poolCompleteness.unruledRemainderEUR)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {Math.abs(poolCompleteness.roundingRemainderEUR) > 0.005 && (
                        <div className="px-5 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500">
                            <Info className="w-3.5 h-3.5 flex-shrink-0" />
                            Cost centres that do have a rule leave{' '}
                            {formatEurExact(poolCompleteness.roundingRemainderEUR)} EUR behind — per-row
                            rounding of the allocation, not stranded cost.
                        </div>
                    )}
                </div>
            )}

            {/* ---- live vs frozen breaks --------------------------------------------- */}
            {frozenTieOut.breaks.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Live vs frozen — {frozenTieOut.breakCount} cell(s) differ
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            A negative difference means the freeze contains an allocation the current
                            rule set no longer produces.
                        </p>
                    </div>
                    <div className="overflow-x-auto max-h-96">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium">Company</th>
                                    <th className="px-4 py-2 text-left font-medium">BU</th>
                                    <th className="px-4 py-2 text-left font-medium">Line</th>
                                    <th className="px-4 py-2 text-right font-medium">Live EUR</th>
                                    <th className="px-4 py-2 text-right font-medium">Frozen EUR</th>
                                    <th className="px-4 py-2 text-right font-medium">Difference</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {frozenTieOut.breaks.map(b => (
                                    <tr key={`${b.companyCode}-${b.buCode}-${b.lineCode}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-700">{b.companyCode}</td>
                                        <td className="px-4 py-2 text-gray-700">{b.buCode}</td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-900">{b.lineCode}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{formatEur(b.liveEUR)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{formatEur(b.frozenEUR)}</td>
                                        <td className={`px-4 py-2 text-right tabular-nums font-semibold ${b.diffEUR < 0 ? 'text-red-600' : 'text-amber-700'}`}>
                                            {formatEur(b.diffEUR)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ---- rule issues -------------------------------------------------------- */}
            {ruleIssues.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">Rule findings</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium">Cost centre</th>
                                    <th className="px-4 py-2 text-left font-medium">Name</th>
                                    <th className="px-4 py-2 text-right font-medium">Rules</th>
                                    <th className="px-4 py-2 text-right font-medium">Sum %</th>
                                    <th className="px-4 py-2 text-left font-medium">Finding</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {ruleIssues.map(r => {
                                    const notes: string[] = [];
                                    if (r.percentSumOff) notes.push(`percentages sum to ${r.sumPercent}, not 100`);
                                    if (r.missingTargetAccount) {
                                        notes.push(`${r.rulesWithoutTargetAccount} rule(s) without a target account — the source account is kept`);
                                    }
                                    if (r.notInBscMap) notes.push('allocates but has no BSCCostCenterMap row');
                                    if (r.notInCostCentreMaster) notes.push('missing from BabProcess.CostCenter');
                                    return (
                                        <tr key={r.costCenterId} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 font-mono text-xs text-gray-900">{r.costCenterId}</td>
                                            <td className="px-4 py-2 text-gray-700">{r.costCenterName ?? '-'}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.activeRuleCount}</td>
                                            <td className={`px-4 py-2 text-right tabular-nums ${r.percentSumOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                                {r.sumPercent}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600">{notes.join('; ')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
